import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Search,
  ArrowUp,
  ArrowDown,
  Info,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import PercentileChart from "./PercentileChart";

// Helper function to get default path data
const getDefaultPathData = (checkoutNumber, checkoutStats) => {
  if (!checkoutStats) return null;
  const paths = checkoutStats[checkoutNumber];
  if (!paths) return null;

  for (const [darts, pathData] of Object.entries(paths)) {
    if (pathData && pathData.default) {
      return {
        path: pathData.default.path,
        mean: pathData.default.mean,
        median: pathData.default.median,
        p10: pathData.default.p10,
        p90: pathData.default.p90,
      };
    }
  }
  return null;
};

// Helper function to get the best path for a given checkout number
const getBestPath = (checkoutNumber, checkoutStats) => {
  if (!checkoutStats) return null;
  const paths = checkoutStats[checkoutNumber];
  if (!paths) return null;

  // Find the path with the lowest mean
  let bestPath = null;
  let lowestMean = Infinity;

  for (const [darts, pathData] of Object.entries(paths)) {
    if (pathData && pathData.mean && pathData.mean < lowestMean) {
      lowestMean = pathData.mean;
      bestPath = { ...pathData, darts };
    }
  }

  return bestPath;
};

function Tooltip({ children, checkoutNumber, checkoutStats, formatPath }) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState("bottom");
  const tooltipRef = React.useRef(null);
  const triggerRef = React.useRef(null);

  const defaultData = getDefaultPathData(checkoutNumber, checkoutStats);
  const bestPath = getBestPath(checkoutNumber, checkoutStats);

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // If there's less than 300px below (tooltip height + buffer), show above
      setPosition(spaceBelow < 450 && spaceAbove > 450 ? "top" : "bottom");
    }
    setIsVisible(true);
  };

  return (
    <div className="relative">
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsVisible(false)}
        className="inline-block"
      >
        {children}
      </div>
      {isVisible && defaultData && (
        <div
          ref={tooltipRef}
          className={`absolute ${
            position === "top" ? "bottom-full mb-2" : "top-full mt-2"
          } left-0 w-80 bg-secondary text-white rounded-lg shadow-lg p-4 z-50 border border-primary/20 transform transition-all duration-200 ease-in-out`}
        >
          <div
            className={`absolute ${
              position === "top" ? "-bottom-2" : "-top-2"
            } left-4 w-4 h-4 bg-secondary transform rotate-45 ${
              position === "top" ? "border-b border-r" : "border-t border-l"
            } border-primary/20`}
          ></div>

          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-primary/20">
            <div className="flex items-center space-x-2">
              <Info size={16} className="text-test" />
              <span className="font-medium text-test">
                {t("statistics.myStrategies.defaultPath")}
              </span>
            </div>
          </div>

          {/* Path Display */}
          <div className="mb-4">
            <div className="bg-secondary p-3 rounded-lg">
              <div className="font-mono text-sm text-white/90">
                {formatPath(defaultData.path)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PersonalizedCheckoutTable({ checkoutStats }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "checkout",
    direction: "desc",
  });
  const [showOnlyOptimized, setShowOnlyOptimized] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeRow, setActiveRow] = useState(null);
  const itemsPerPage = 50;

  // Early return if checkoutStats is not available
  if (!checkoutStats) {
    return (
      <div className="text-center py-8">
        <p className="text-white/70">
          {t("statistics.loading") || "Loading..."}
        </p>
      </div>
    );
  }

  // Helper function to format the checkout path
  const formatPath = (path) => {
    return path.split(", ").map((segment, index, array) => (
      <React.Fragment key={index}>
        <span className="text-white">{segment}</span>
        {index < array.length - 1 && (
          <span className="mx-1 text-white/30">→</span>
        )}
      </React.Fragment>
    ));
  };

  // Create rows for the table
  const rows = useMemo(() => {
    const result = [];
    for (let i = 2; i <= 170; i++) {
      const bestPath = getBestPath(i.toString(), checkoutStats);
      if (bestPath) {
        result.push({
          checkout: i,
          path: bestPath.path,
          median: bestPath.median,
          p10: bestPath.p10,
          p90: bestPath.p90,
          isChanged: bestPath.changed,
          improvement: bestPath.mean_diff_pct
            ? bestPath.mean_diff_pct.toFixed(1)
            : 0,
        });
      }
    }
    return result;
  }, [checkoutStats]);

  // Filter and sort rows
  const filteredAndSortedRows = useMemo(() => {
    let filtered = rows;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (row) =>
          row.checkout.toString().includes(searchTerm) ||
          row.path.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply optimized filter
    if (showOnlyOptimized) {
      filtered = filtered.filter((row) => row.isChanged);
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      if (sortConfig.key === "path") {
        return sortConfig.direction === "asc"
          ? a.path.localeCompare(b.path)
          : b.path.localeCompare(a.path);
      }

      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (sortConfig.direction === "asc") {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });
  }, [rows, searchTerm, sortConfig, showOnlyOptimized]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredAndSortedRows.length / itemsPerPage);
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedRows.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedRows, currentPage]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to top of the table
      const tableElement = document.querySelector(".checkout-table");
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  // Reset to first page when search or filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showOnlyOptimized]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <ArrowUp size={16} />
    ) : (
      <ArrowDown size={16} />
    );
  };

  // Add click handler for the table
  const handleTableClick = (e) => {
    // If clicking outside of a row, clear the active row
    if (!e.target.closest("tr")) {
      setActiveRow(null);
    }
  };

  return (
    <div
      className="bg-secondary/30 rounded-lg p-4 sm:p-6 shadow-lg checkout-table"
      onClick={handleTableClick}
    >
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h3 className="text-white text-lg sm:text-xl font-semibold">
          {t("statistics.myStrategies.personalizedCheckouts")}
        </h3>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50"
              size={16}
            />
            <input
              type="text"
              placeholder={t("statistics.myStrategies.search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-secondary/50 text-white pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-test/50 transition-all duration-200"
            />
          </div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyOptimized}
              onChange={(e) => setShowOnlyOptimized(e.target.checked)}
              className="form-checkbox h-4 w-4 text-test border-secondary rounded focus:ring-test/50 transition-colors duration-200"
            />
            <span className="text-white text-sm">
              {t("statistics.myStrategies.onlyOptimized")}
            </span>
          </label>
        </div>
      </div>

      <p className="text-white/80 mb-6 text-xs sm:text-sm">
        {t("statistics.myStrategies.personalizedCheckoutsDescription")}
      </p>

      {/* Desktop Table View */}
      <div className="hidden sm:block overflow-x-auto -mx-2 sm:mx-0">
        <div className="min-w-[800px] sm:min-w-0 overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
              <tr className="text-left border-b-2 border-primary/20">
                <th
                  className="py-2 px-3 text-test font-medium text-xs sm:text-sm cursor-pointer hover:bg-secondary/20 text-xs sm:text-sm transition-colors duration-200 w-16 min-w-[60px] text-left"
                  onClick={() => requestSort("checkout")}
                >
                  <div className="flex items-center space-x-2">
                    <span>{t("statistics.myStrategies.checkout")}</span>
                    {getSortIcon("checkout")}
                  </div>
                </th>
                <th
                  className="py-2 px-3 text-test font-medium text-xs sm:text-sm cursor-pointer hover:bg-secondary/20 text-xs sm:text-sm transition-colors duration-200 w-[40%] min-w-[120px] text-left"
                  onClick={() => requestSort("path")}
                >
                  <div className="flex items-center space-x-2">
                    <span>{t("statistics.myStrategies.path")}</span>
                    {getSortIcon("path")}
                  </div>
                </th>
                <th
                  className="py-2 px-3 text-test font-medium text-xs sm:text-sm cursor-pointer hover:bg-secondary/20 text-xs sm:text-sm transition-colors duration-200 w-20 min-w-[60px] text-left"
                  onClick={() => requestSort("improvement")}
                >
                  <div className="flex items-center space-x-2">
                    <span>Improvement</span>
                    {getSortIcon("improvement")}
                  </div>
                </th>
                <th
                  className="py-2 px-3 text-test font-medium text-xs sm:text-sm cursor-pointer hover:bg-secondary/20 text-xs sm:text-sm transition-colors duration-200 w-[30%] min-w-[100px] text-left"
                  onClick={() => requestSort("median")}
                >
                  <div className="flex items-center space-x-2">
                    <span>{t("statistics.myStrategies.performance")}</span>
                    {getSortIcon("median")}
                    <div className="relative group">
                      <Info size={16} className="text-test/70 cursor-help" />
                      <div className="absolute right-full top-full mb-2 mr-2 w-64 bg-secondary text-white rounded-lg shadow-lg p-4 z-50 border border-primary/20 transform transition-all duration-200 ease-in-out opacity-0 group-hover:opacity-100 pointer-events-none">
                        <div className="absolute -top-2 -right-2 w-4 h-4 bg-secondary transform rotate-45 border-r border-b border-primary/20"></div>
                        <div className="text-sm">
                          <p className="mb-2">
                            {t("statistics.myStrategies.performanceTooltip")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary/10">
              {paginatedRows.map((row, index) => (
                <tr
                  key={row.checkout}
                  onClick={() => setActiveRow(row.checkout)}
                  className={`transition-all duration-200 ${
                    row.isChanged ? "bg-test/5" : ""
                  } ${
                    index === paginatedRows.length - 1 ? "rounded-b-lg" : ""
                  } ${
                    activeRow === row.checkout
                      ? "bg-secondary/40 shadow-lg"
                      : "hover:bg-secondary/30 hover:shadow-md"
                  }`}
                >
                  <td className="py-2 px-4 text-white font-medium text-xs sm:text-sm w-24">
                    <div className="flex items-center">
                      <span className="bg-secondary/50 px-3 py-1.5 rounded-md text-test">
                        {row.checkout}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 px-4 w-1/2">
                    <div className="flex items-center space-x-2">
                      <div className="bg-secondary/40 px-2 py-1 rounded-md font-mono text-xs truncate">
                        {formatPath(row.path)}
                      </div>
                      {row.isChanged && (
                        <Tooltip
                          checkoutNumber={row.checkout.toString()}
                          checkoutStats={checkoutStats}
                          formatPath={formatPath}
                        >
                          <span className="text-xs bg-test/20 text-test px-2 py-1 rounded-full cursor-help transition-colors duration-200 hover:bg-secondary whitespace-nowrap">
                            {t("statistics.myStrategies.optimized")}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-4 w-32">
                    <div className="flex items-center">
                      {row.isChanged && row.improvement > 0 && (
                        <span className="text-sm font-medium text-green-400 bg-green-400/10 px-2 py-1 rounded-md whitespace-nowrap">
                          +{row.improvement}%
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-4 w-1/3">
                    <PercentileChart
                      lowerPercentile={row.p10}
                      averagePercentile={row.median}
                      upperPercentile={row.p90}
                      height="0.75rem"
                      compact={true}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Sorting Toggle - Only for Checkout */}
      <div className="sm:hidden mb-4 flex items-center gap-2">
        <span className="text-white text-sm">{t("Sort by checkout")}:</span>
        <button
          onClick={() => requestSort("checkout")}
          className={`text-xs px-2 py-1 rounded-md border ${
            sortConfig.key === "checkout"
              ? "bg-primary text-white border-primary"
              : "bg-secondary/40 text-white border-secondary/60"
          }`}
        >
          {sortConfig.direction === "asc" ? "↑" : "↓"}
        </button>
      </div>

      {/* Mobile Card View - Compact and Expandable */}
      <div className="sm:hidden space-y-2">
        {paginatedRows.map((row) => {
          const rowKey = row.checkout.toString(); // Normalize key to string
          const isExpanded = activeRow === rowKey;

          return (
            <div
              key={rowKey}
              onClick={() => setActiveRow(isExpanded ? null : rowKey)}
              className={`bg-secondary/20 rounded-md p-2 transition-all duration-200 cursor-pointer ${
                isExpanded ? "bg-secondary/40 shadow-md" : ""
              } ${row.isChanged ? "border-l-4 border-test" : ""}`}
            >
              {/* Row Header */}
              <div className="flex items-center gap-2 flex-nowrap overflow-hidden">
                {/* Left section */}
                <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                  <span className="text-test text-sm font-medium shrink-0">
                    {row.checkout}
                  </span>
                  <div className="bg-secondary/50 px-2 py-0.5 rounded font-mono text-xs text-white truncate max-w-[120px]">
                    {row.path}
                  </div>
                </div>

                {/* Right section */}
                <div className="flex items-center gap-2 ml-auto shrink-0">
                  {row.improvement > 0 && (
                    <span className="text-xs font-medium text-green-400 bg-green-400/10 px-1.5 py-0.5 rounded">
                      +{row.improvement}%
                    </span>
                  )}
                  <div className="w-24 h-8">
                    <PercentileChart
                      lowerPercentile={row.p10}
                      averagePercentile={row.median}
                      upperPercentile={row.p90}
                      tiny
                      height="0.3rem"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination Controls - Bottom */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-2 rounded-md transition-colors duration-200 ${
              currentPage === 1
                ? "bg-primary/50 text-white/50 cursor-not-allowed"
                : "bg-secondary/50 text-white hover:bg-secondary/70"
            }`}
          >
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              const startIdx = (pageNum - 1) * itemsPerPage;
              const endIdx = Math.min(
                startIdx + itemsPerPage,
                filteredAndSortedRows.length
              );
              const firstCheckout =
                filteredAndSortedRows[startIdx]?.checkout ?? "-";
              const lastCheckout =
                filteredAndSortedRows[endIdx - 1]?.checkout ?? "-";

              const label = `${firstCheckout}–${lastCheckout}`;

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 rounded-md transition-colors duration-200 ${
                    currentPage === pageNum
                      ? "bg-primary text-white"
                      : "bg-secondary/50 text-white hover:bg-secondary/70"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-md transition-colors duration-200 ${
              currentPage === totalPages
                ? "bg-primary/50 text-white/50 cursor-not-allowed"
                : "bg-secondary/50 text-white hover:bg-secondary/70"
            }`}
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="text-white/70 text-sm">
          Checkouts:{" "}
          {paginatedRows.length > 0 ? (
            <>
              {paginatedRows[0].checkout} -{" "}
              {paginatedRows[paginatedRows.length - 1].checkout}
            </>
          ) : (
            t("statistics.myStrategies.noResults")
          )}
        </div>
      </div>
    </div>
  );
}

export default PersonalizedCheckoutTable;
